import { FC, useMemo } from 'react';
import styled, { keyframes } from 'styled-components/macro';
import { ChartDonutSection, TotalFootprintValue } from './components';
import { Co2FootprintVO } from 'vo';
import { useTranslation } from 'react-i18next';
import { CalculatorFormValues } from 'common/types';
import { ReductionOpportunities } from '../ReductionOpportunities';
import { RedAccentHeading } from 'components';
import { DEVICE, TYPOGRAPHY } from '@net0-calc/common/assets/styles';
import { formatWeightValue } from 'utils';

interface TopContainerProps {
  co2FootprintSumVO: Co2FootprintVO;
  co2FootprintsVOs: {
    fromTo: Co2FootprintVO;
    fromAirportToCity: Co2FootprintVO;
    accomodation: Co2FootprintVO;
    aroundCity: Co2FootprintVO;
  };
  formValues: CalculatorFormValues;
}

export const TopContainer: FC<TopContainerProps> = ({ co2FootprintSumVO, co2FootprintsVOs, formValues }) => {
  const { t } = useTranslation();

  const travelToMonacoCo2FootpintVO = Co2FootprintVO.sum([co2FootprintsVOs.fromTo, co2FootprintsVOs.fromAirportToCity]);
  const title = t(`personal.pages.result.topContainer.${formValues.peopleCount === 1 ? 'titleOnePerson' : 'title'}`);
  const fromAddress = formValues.fromLocation?.mainAddress || 'Karaganda';

  const totalKg = co2FootprintSumVO.total.kg;
  const breakdownItems = useMemo(() => {
    const items = [
      {
        color: 'var(--chart-color2)',
        label: t('personal.pages.result.topContainer.marks.travelAround'),
        value: co2FootprintsVOs.aroundCity.total.stringifiedValue,
        kg: co2FootprintsVOs.aroundCity.total.kg,
      },
      {
        color: 'var(--chart-color3)',
        label: t('personal.pages.result.topContainer.marks.accommodation'),
        value: co2FootprintsVOs.accomodation.total.stringifiedValue,
        kg: co2FootprintsVOs.accomodation.total.kg,
      },
      {
        color: 'var(--chart-color4)',
        label: t('personal.pages.result.topContainer.marks.travelTo', { fromAddress }),
        value: travelToMonacoCo2FootpintVO.total.stringifiedValue,
        kg: travelToMonacoCo2FootpintVO.total.kg,
      },
    ];
    return items;
  }, [co2FootprintsVOs, travelToMonacoCo2FootpintVO, fromAddress, t]);

  return (
    <Root>
      <SectionLeft>
        <RedAccentHeading>
          <Title>
            <span dangerouslySetInnerHTML={{ __html: title }}></span>
            <TotalFootprintValue value={co2FootprintSumVO.total.kg} />
          </Title>
        </RedAccentHeading>

        {/* Hero stat cards */}
        <StatCardsRow>
          <StatCard>
            <StatValue>{formatWeightValue(co2FootprintSumVO.total.kg)}</StatValue>
            <StatLabel>{t('personal.pages.result.topContainer.subTitle')}</StatLabel>
          </StatCard>
          <StatCard>
            <StatValue>{formatWeightValue(co2FootprintSumVO.perPerson.kg)}</StatValue>
            <StatLabel>{t('personal.pages.result.perPerson')}</StatLabel>
          </StatCard>
        </StatCardsRow>

        <MobileChartDonutSection {...{ fromAddress, co2FootprintSumVO, co2FootprintsVOs }} />
        <BreakdownTitle>{t('personal.pages.result.topContainer.subTitle')}</BreakdownTitle>
        <BreakdownList>
          {breakdownItems.map(item => {
            const pct = totalKg > 0 ? Math.round((item.kg / totalKg) * 100) : 0;
            return (
              <BreakdownCard key={item.label}>
                <BreakdownCardTop>
                  <BreakdownLabelRow>
                    <Mark $color={item.color} />
                    <BreakdownLabel>{item.label}</BreakdownLabel>
                  </BreakdownLabelRow>
                  <BreakdownValue>{item.value}</BreakdownValue>
                </BreakdownCardTop>
                <ProgressTrack>
                  <ProgressFill $color={item.color} $width={pct} />
                </ProgressTrack>
                <BreakdownPct>{pct}%</BreakdownPct>
              </BreakdownCard>
            );
          })}
        </BreakdownList>
        <ReductionOpportunities formValues={formValues} />
      </SectionLeft>
      <DesktopChartDonutWrap>
        <ChartDonutSection {...{ fromAddress, co2FootprintSumVO, co2FootprintsVOs }} />
      </DesktopChartDonutWrap>
    </Root>
  );
};

const fadeSlideUp = keyframes`
  from { opacity: 0; transform: translateY(24px); }
  to { opacity: 1; transform: translateY(0); }
`;

const scaleIn = keyframes`
  from { opacity: 0; transform: scale(0.92); }
  to { opacity: 1; transform: scale(1); }
`;

const Root = styled.div`
  width: 100%;
  display: flex;
  flex-direction: column;
  align-items: stretch;
  margin-bottom: 40px;
  order: 1;

  @media ${DEVICE.tablet} {
    flex-direction: row;
    align-items: flex-start;
    justify-content: space-between;
    gap: 40px;
    margin-bottom: 60px;
  }
`;

const SectionLeft = styled.div`
  max-width: 850px;
  width: 100%;
`;

const Title = styled.h1`
  ${TYPOGRAPHY.titleSemibold28}
  color: var(--color6);
  margin-bottom: 24px;

  @media ${DEVICE.laptop} {
    ${TYPOGRAPHY.titleSemibold44}
    margin-bottom: 36px;
  }
`;

/* --- Stat hero cards --- */
const StatCardsRow = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 16px;
  margin-bottom: 32px;
  opacity: 0;
  animation: ${fadeSlideUp} 0.6s cubic-bezier(0.16, 1, 0.3, 1) 0.2s forwards;

  @media ${DEVICE.laptop} {
    gap: 24px;
    margin-bottom: 48px;
  }
`;

const StatCard = styled.div`
  background: var(--color5);
  border: 1px solid var(--color10);
  border-radius: 16px;
  padding: 20px 16px;
  text-align: center;
  transition: box-shadow 0.3s ease, transform 0.2s ease;

  &:hover {
    box-shadow: 0 4px 20px rgba(0, 0, 0, 0.06);
    transform: translateY(-2px);
  }

  @media ${DEVICE.laptop} {
    padding: 28px 24px;
  }
`;

const StatValue = styled.div`
  ${TYPOGRAPHY.titleSemibold28}
  color: var(--color2);
  margin-bottom: 4px;

  @media ${DEVICE.laptop} {
    ${TYPOGRAPHY.titleSemibold36}
  }
`;

const StatLabel = styled.div`
  ${TYPOGRAPHY.captionRegular12}
  color: var(--color9);
  text-transform: uppercase;
  letter-spacing: 0.06em;

  @media ${DEVICE.laptop} {
    ${TYPOGRAPHY.bodyRegular14}
  }
`;

/* --- Breakdown --- */
const BreakdownTitle = styled.h3`
  ${TYPOGRAPHY.bodyMedium16}
  color: var(--color6);
  margin-bottom: 16px;

  @media ${DEVICE.laptop} {
    ${TYPOGRAPHY.bodyMedium20}
    margin-bottom: 20px;
  }
`;

const BreakdownList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
  margin-bottom: 40px;

  @media ${DEVICE.laptop} {
    gap: 16px;
    margin-bottom: 60px;
  }
`;

const BreakdownCard = styled.div`
  background: var(--color5);
  border: 1px solid var(--color10);
  border-radius: 12px;
  padding: 16px;
  transition: box-shadow 0.3s ease;

  &:hover {
    box-shadow: 0 2px 12px rgba(0, 0, 0, 0.05);
  }

  @media ${DEVICE.laptop} {
    padding: 20px 24px;
  }
`;

const BreakdownCardTop = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 10px;
`;

const BreakdownLabelRow = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
`;

const Mark = styled.span<{ $color: string }>`
  width: 14px;
  height: 14px;
  border-radius: 4px;
  background-color: ${({ $color }) => $color};
  flex-shrink: 0;
`;

const BreakdownLabel = styled.span`
  ${TYPOGRAPHY.bodyRegular14}
  color: var(--color6);

  @media ${DEVICE.laptop} {
    ${TYPOGRAPHY.bodyRegular16}
  }
`;

const BreakdownValue = styled.span`
  ${TYPOGRAPHY.bodyMedium16}
  color: var(--color6);
  white-space: nowrap;

  @media ${DEVICE.laptop} {
    ${TYPOGRAPHY.bodyMedium20}
  }
`;

const ProgressTrack = styled.div`
  width: 100%;
  height: 6px;
  background: var(--color8);
  border-radius: 3px;
  overflow: hidden;
  margin-bottom: 6px;
`;

const ProgressFill = styled.div<{ $color: string; $width: number }>`
  height: 100%;
  width: ${({ $width }) => $width}%;
  background: ${({ $color }) => $color};
  border-radius: 3px;
  transition: width 0.8s cubic-bezier(0.4, 0, 0.2, 1);
`;

const BreakdownPct = styled.span`
  ${TYPOGRAPHY.captionRegular12}
  color: var(--color9);
`;

const MobileChartDonutSection = styled(ChartDonutSection)`
  display: block;
  margin-bottom: 24px;

  @media ${DEVICE.tablet} {
    display: none;
  }
`;

const DesktopChartDonutWrap = styled.div`
  display: none;

  @media ${DEVICE.tablet} {
    display: block;
    flex-shrink: 0;
    opacity: 0;
    animation: ${scaleIn} 0.7s cubic-bezier(0.16, 1, 0.3, 1) 0.35s forwards;
  }
`;
